import {
  useGetCatalogProducts,
  getGetCatalogProductsQueryOptions,
} from "@/requests/gen/react-query/catalog-product";
import { ProductTypeEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Archive, FlaskConical, Plus } from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import { Badge } from "@/components/ui/badge";
import { keepPreviousData } from "@tanstack/react-query";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { resolveProductImage } from "@/lib/default-image";
import { Header } from "@/components/universal/header";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { Toggle } from "@/components/ui/toggle";
import { exportCatalogProducts } from "@/requests/gen/react-query/catalog-product";

import { ExportCSV } from "@/components/ui/csv-export";
import { useQueryStates, type inferParserType } from "nuqs";

type CatalogProductSearch = inferParserType<typeof dataTableSearchParsers>;

function catalogProductQueryParams(
  p: CatalogProductSearch,
): Parameters<typeof getGetCatalogProductsQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    product_types: p.product_types || [],
    entity_ids: p.entity_ids,
    is_deleted: p.is_deleted,
    only_samples: p.only_samples,
  };
}

export const Route = createFileRoute("/_private/catalog-product/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetCatalogProductsQueryOptions(
        catalogProductQueryParams(loadDataTableSearch(location.searchStr)),
      ),
    ),
  component: CatalogProductLookup,
});

// Define the columns for the table
interface CatalogProductTableProps {
  link?: LinkProps;
  image: string;
  name: string;
  product_type: ProductTypeEnum;
  treatment: string;
  visible_entities: string[];
  fulfiller?: string;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<CatalogProductTableProps>[] = [
  {
    header: "",
    accessorKey: "image",
    cell: ({ row }) => (
      <img
        src={row.original.image}
        alt="Product"
        className="h-12 w-12 rounded-sm object-contain"
      />
    ),
  },
  { header: "Name", accessorKey: "name" },
  { header: "Type", accessorKey: "product_type" },
  { header: "Treatment", accessorKey: "treatment" },
  {
    header: "Entities",
    accessorKey: "visible_entities",
    cell: (cell) => {
      const entities = cell.getValue() as string[];

      const firstEntity = entities.at(0);
      const remainingCount = entities.length - 1;

      return (
        <div className="flex items-center gap-1">
          {firstEntity && <Badge variant={"secondary"}>{firstEntity}</Badge>}
          {remainingCount > 0 && (
            <Tooltip>
              <TooltipTrigger className="bg-primary rounded-full text-white size-5 flex items-center justify-center text-xs">
                +{remainingCount}
              </TooltipTrigger>
              <TooltipContent>
                {entities
                  .slice(1)
                  .map((entity) => entity)
                  .join(", ")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    header: "Fulfiller",
    accessorKey: "fulfiller",
  },
];

// Main component
function CatalogProductLookup() {
  const [queryStates, setQueryStates] = useQueryStates(dataTableSearchParsers);

  // Get the products
  const catalog_products = useGetCatalogProducts(
    catalogProductQueryParams(queryStates),
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const [entitySearch, setEntitySearch] = useState<string | undefined>();
  const entities = useGetEntitiesInfinite(
    {
      search: entitySearch,
      include_ids: queryStates.entity_ids,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  // Map the data to the table format
  const tableData: CatalogProductTableProps[] | undefined =
    catalog_products.data?.items.map((catalog_product) => {
      return {
        link: linkOptions({
          to: "/catalog-product/$catalogProductId",
          params: {
            catalogProductId: String(catalog_product.catalog_product_id),
          },
        }),
        image: resolveProductImage(catalog_product),
        name: `${catalog_product.is_sample ? "SAMPLE - " : ""}${catalog_product.display_name}`,
        product_type: catalog_product.product_base.product_type,
        treatment: catalog_product.product_base.treatment.name,
        visible_entities: catalog_product.catalog_product_visibility.map(
          (product) => product.entity.name,
        ),
        fulfiller: catalog_product?.fulfiller?.name,
      };
    });

  return (
    <div>
      <title>Catalog Products - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Catalog", link: { to: "/catalog-product/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Catalog</CardTitle>
          <CardDescription>
            View products from Legrande's catalog below. You can search, filter,
            and click to see more details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/catalog-product/create">
                <Plus />
                Create Catalog Product
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={catalog_products.isFetching} />
            <DropdownFilter
              title="Type"
              options={[
                {
                  items: Object.values(ProductTypeEnum).map((product_type) => {
                    return { label: product_type, value: product_type };
                  }),
                },
              ]}
              values={queryStates.product_types || []}
              onChange={(values) => setQueryStates({ product_types: values })}
            />
            <DropdownFilter
              title="Entity"
              options={[
                {
                  items:
                    entities.data?.pages
                      .flatMap((page) => page.items)
                      .map((entity) => {
                        return {
                          label: entity.name,
                          value: entity.entity_id,
                        };
                      }) ?? [],
                },
              ]}
              values={queryStates.entity_ids}
              onChange={(values) => setQueryStates({ entity_ids: values })}
              search={entitySearch}
              setSearch={setEntitySearch}
              isFetching={entities.isFetchingNextPage}
              fetchNextPage={entities.fetchNextPage}
            />
            <Toggle
              pressed={queryStates.is_deleted || false}
              onPressedChange={(pressed) => {
                setQueryStates({
                  is_deleted: pressed,
                });
              }}
            >
              <Archive />
              Archived
            </Toggle>
            <Toggle
              pressed={queryStates.only_samples}
              onPressedChange={(pressed) =>
                setQueryStates({ only_samples: pressed })
              }
            >
              <FlaskConical />
              Samples
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={catalog_products.isLoading}
            isError={catalog_products.isError}
            count={catalog_products.data?.count}
            footerActions={
              <ExportCSV
                onExport={() =>
                  exportCatalogProducts({
                    search: queryStates.search,
                    sort_column: queryStates.sort_column,
                    ascending: queryStates.ascending,
                    product_types: queryStates.product_types || [],
                    entity_ids: queryStates.entity_ids,
                    is_deleted: queryStates.is_deleted,
                    only_samples: queryStates.only_samples,
                    hidden_columns: queryStates.hidden,
                  })
                }
                filename="catalog-products"
                requiresPHIWarning={false}
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
